<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        Button,
        CardLayout,
        NotificationModal,
        PageLoading,
        Pagination,
        SearchSelect,
        SvelteTable,
    } from "../../../Components/Common/index.svelte";

    import { isMobile, user } from "../../../stores";

    import {
        getAccountCalendarEventType,
        getAccountCalendarRepeatIntervalType,
        getBackEndDeleteType,
        getDateFromDateString,
        getFormatDate,
        getText,
        replaceURL,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        accountCalendarEventType,
        CalendarEventCategoryTypes,
        DeleteRepeatingEventOptions,
        pageOptions,
    } from "../../../Utils/utilEnums.svelte";

    import { addIcon, deleteIcon, editIcon } from "../../../Utils/utilConstants.svelte";

    import ModifyAccountEventModal from "./ModifyAccountEventModal.svelte";
    import SearchInput from "../../../Components/Common/SearchInput.svelte";
    import SearchDateRangeInput from "../../../Components/Common/SearchDateRangeInput.svelte";

    export let params;
    export let accountId = "";

    let isGettingAccountCalendarEvents = false,
        returnedAccountCalendarEvents = [];

    let deleteAccountCalendarEventModalOpen = false,
        selectedAccountCalendarEventID = Number(0),
        isDeletingAccountCalendarEvent = false;

    let filters = { doctorId: accountId, eventType: "all", dateFrom: new Date(), dateTo: new Date() };

    let page = 0,
        pageSize = 25,
        count;

    let isModifyAccountCalendarEventModalOpen = false,
        eventId,
        newValues = {};

    let deleteType,
        eventToDelete;

    $: isDeleteEventButtonDisabled =
        eventToDelete &&
        eventToDelete.repeatIntervalType &&
        eventToDelete.eventType &&
        !deleteType;

    const columns = [
        {
            key: "fromDate",
            title: "From Date",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getFormatDate(v.fromDate),
        },
        {
            key: "toDate",
            title: "To Date",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getFormatDate(v.toDate),
        },
        {
            key: "eventType",
            title: "Event Type",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getAccountCalendarEventType(v.eventType),
        },
        {
            key: "hospital",
            title: "Hospital",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getText(v.hospitalName),
        },
        {
            key: "surgeon",
            title: "Surgeon",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getText(v.surgeonName),
        },
        {
            key: "eventCategory",
            title: "Event Category",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            renderValue: (v) => renderEventCategory(v),
        },
        {
            key: "description",
            title: "Description",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => getText(v.description),
        },
        {
            key: "recurrence",
            title: "Recurrence",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-default",
            value: (v) => _getRecurrentType(v),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "px-3 cursor-default",
            renderValue: (v) => _renderActionIcons(),
        },
    ];

    onMount(() => {
        if (params.query) _updateSearchParamsByQuery(params.query);

        loadAccountCalendarEvent(page * pageSize, pageSize, _getSearchParams());
    });

    const loadAccountCalendarEvent = async (start, limit, params) => {
        isGettingAccountCalendarEvents = true;

        filters.accountId = accountId;

        await apiMethods.getMethod
            .getAccountCalendarEvents(start, limit, params)
            .then((res) => _handleAccountCalendarEvents(res))
            .catch((err) => showException(err));

        isGettingAccountCalendarEvents = false;
    };

    const renderEventCategory = (v) => {
        if (v.eventCategory && v.eventCategoryName && v.eventCategoryColour) {
            return `
                <span style="color: ${v.eventCategoryColour ?? black};">${v.eventCategoryName}</span>
            `;
        }
        return "-";
    }

    const deleteAccountCalendarEvent = async () => {
        isDeletingAccountCalendarEvent = true;

        await apiMethods.deleteMethod
            .deleteAccountCalendarEvent(selectedAccountCalendarEventID,
                getBackEndDeleteType(deleteType)
            )
            .then((res) => {
                if (!res || !res.id)
                    showError(
                        "Failed to delete calendar event, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    loadAccountCalendarEvent(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
            })
            .catch((err) => showException(err));

        _closeDeleteAccountLeaveModal();

        isDeletingAccountCalendarEvent = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadAccountCalendarEvent(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/Account/view/${params.id}/tab=accountCalendarEvent&page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleClickCell = (event, rowData, key) => {
        const { className } = event.target;

        if (key == "buttons") {
            if (className.includes("deleteIcon"))
                _openDeleteAccountLeaveModal(rowData);
            else if (className.includes("editIcon"))
                _openModifyAccountCalendarEvent(false, rowData.id);
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAccountCalendarEvent(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAccountCalendarEvent(0, pageSize, _getSearchParams());
    };

    // HELPER FUNCTIONS
    const _handleAccountCalendarEvents = (res) => {
        if (!res)
            showError(
                "Failed to load leaves data, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.error) showError(res.error);
        else {
            returnedAccountCalendarEvents = [...res.results];
            count = res.total;
        }
    };

    const _updateSearchParamsByQuery = (query) => {
        const arr = query.split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();

                if (arr[i].includes("eventType="))
                    filters.eventType = Number(arr[i].split("=")[1].toString());
            }
        }
    };

    const _getSearchParams = () => {
        let params = {
            sort: "fromdatedesc",
        };

        const dateFrom = getDateFromDateString(filters.dateFrom)
            .split(", ")[0]
            .split("/")
            .reverse()
            .join("-");

        const dateTo =  getDateFromDateString(filters.dateTo)
            .split(", ")[0]
            .split("/")
            .reverse()
            .join("-");

        if (dateFrom.includes("NaN") || dateTo.includes("NaN")) return location.reload();

        if (filters.search) params.search = filters.search;

        if (accountId) params["filter[doctorId]"] = accountId;

        if (
            (filters.eventType && filters.eventType != "all") ||
            filters.eventType == 0
        )
            params["filter[eventType]"] = filters.eventType;
        
        if (dateFrom )params['filter[fromDateFrom]'] = dateFrom;

        if (dateTo) params['filter[toDateTo]'] = dateTo; 

        return params;
    };

    const _getParamsString = () => {
        let str = "";

        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[eventType]"] || params["filter[eventType]"] == 0)
                str += `&eventType=${params["filter[eventType]"]}`;
        }

        return str;
    };

    const _getRecurrentType = (event) => {
        const { repeatIntervalType } = event;

        if (!repeatIntervalType) return "-";

        return getAccountCalendarRepeatIntervalType(repeatIntervalType);
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Event">
                        <i class="editIcon cursor-pointer ${editIcon}"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Event">
                        <i class="deleteIcon cursor-pointer ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _openModifyAccountCalendarEvent = (isNew = true, id = Number(0)) => {
        if (isNew) {
            newValues = {
                eventType: CalendarEventCategoryTypes.List,
                doctorId: accountId
            };
            eventId = null;
        } else {
            eventId = id;
            newValues = {};
        }

        isModifyAccountCalendarEventModalOpen = true;
    };

    const _closeModifyAccountCalendarEvent = () => {
        isModifyAccountCalendarEventModalOpen = false;

        eventId = undefined;
        newValues = {};
    };

    const _openDeleteAccountLeaveModal = (row) => {
        eventToDelete = row;
        deleteAccountCalendarEventModalOpen = true;
        selectedAccountCalendarEventID = Number(row.id);
    };

    const _closeDeleteAccountLeaveModal = () => {
        deleteAccountCalendarEventModalOpen = false;
        deleteType = undefined;
        eventToDelete = undefined;

        selectedAccountCalendarEventID = Number(0);
    };

    const btnOptions = [
        {
            iconName: "addIcon",
            tooltip: "Create Event",
            iFont: addIcon,
            iconFunction: _openModifyAccountCalendarEvent,
            btnText: "Create",
        }
    ]
</script>

<div class="flex flex-col overflow-y-auto flex-1 gap-4 m-4" >
    <CardLayout
        title="Calendar Events"
        {btnOptions}
        flex1
        enableCollapse={false}
    >
        <div
            class="flex flex-0 px-4 py-3 {$isMobile
                ? 'flex-col gap-y-4 items-start'
                : 'justify-between items-end'}"
        >
            <div class="flex gap-4 flex-wrap">
                <SearchSelect
                    title="Event Type"
                    inputClass="bg-[#fff]"
                    bind:value={filters.eventType}
                    onChangeFunc={handleFilterSelect}
                    isDisabled={isGettingAccountCalendarEvents}
                >
                    <option value="all">All Types</option>
                    {#each accountCalendarEventType as aceType}
                        {#if aceType.doctorSpecific}
                            <option value={aceType.id}>{aceType.type}</option>
                        {/if}
                    {/each}
                </SearchSelect>

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.dateFrom}
                        bind:endDate={filters.dateTo}
                        title="Date Range"
                        inputClass="borderB bg-[#fff] w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disabled={isGettingAccountCalendarEvents}
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.dateFrom}
                        title="Date from"
                        type="date"
                        isLong
                        inputClass="borderB bg-[#fff]"
                        inputFunc={handleFilterSelect}
                        isDisabled={isGettingAccountCalendarEvents}
                        maxDate={filters.dateTo ?? undefined}
                    />

                    <SearchInput
                        bind:value={filters.dateTo}
                        title="Date to"
                        type="date"
                        isLong
                        inputClass="borderB bg-[#fff]"
                        inputFunc={handleFilterSelect}
                        isDisabled={isGettingAccountCalendarEvents}
                        minDate={filters.dateFrom ?? undefined}
                    />
                {/if}
            </div>
        </div>

        <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
            {#if isGettingAccountCalendarEvents}
                <PageLoading />
            {:else if returnedAccountCalendarEvents.length}
                <div
                    class={$isMobile
                        ? "overflow-x-auto overflow-y-visible"
                        : "overflow-y-auto absolute inset-0"}
                >
                    <SvelteTable
                        {columns}
                        rows={returnedAccountCalendarEvents}
                        clickCellFunc={({
                            detail: { event, row, key },
                        }) => {
                            handleClickCell(event, row, key);
                        }}
                    />
                </div>
            {:else}
                <div class="p-6">
                    No calendar events in this period
                </div>
            {/if}
        </div>

        {#if !filters.dateTo && !filters.dateFrom}
            <div class="flex flex-col px-4 py-3">
                <div class="flex flex-1 justify-between items-center">
                    <div class="flex items-center gap-x-4">
                        <div
                            class="flex items-center {$isMobile
                                ? 'flex-col'
                                : 'flex-row'}"
                        >
                            <strong class="text-gray-600 text-sm"
                                >Table size:</strong
                            >
                            <select
                                class="select select-sm text-gray-600 {$isMobile
                                    ? 'm-2'
                                    : 'mx-2'}"
                                bind:value={pageSize}
                                on:change|preventDefault={() =>
                                    onPageSizeChange(pageSize)}
                            >
                                {#each pageOptions as pageOption}
                                    <option
                                        value={pageOption.amount}
                                        selected={pageOption.amount ===
                                            pageSize}
                                    >
                                        {pageOption.amount}</option
                                    >
                                {/each}
                            </select>
                        </div>
                    </div>

                    {#if returnedAccountCalendarEvents.length}
                        <Pagination
                            {page}
                            {pageSize}
                            {count}
                            on:onPageChange={onPageChange}
                        />
                    {/if}
                </div>
            </div>
        {/if}
    </CardLayout>
</div>

<ModifyAccountEventModal
    id={eventId}
    {newValues}
    isModalOpen={isModifyAccountCalendarEventModalOpen}
    closeModal={_closeModifyAccountCalendarEvent}
    afterSubmit={() =>
        loadAccountCalendarEvent(page * pageSize, pageSize, _getSearchParams())}
/>

<NotificationModal
    title="Delete Event"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    cancelText={isDeletingAccountCalendarEvent ? "Deleting" : "Confirm"}
    isModalOpen={deleteAccountCalendarEventModalOpen}
    isConfirming={isDeletingAccountCalendarEvent}
    confirmAction={_closeDeleteAccountLeaveModal}
    closeModal={deleteAccountCalendarEvent}
    isReverseBtns={true}
    closeModalFunction={_closeDeleteAccountLeaveModal}
    isCancelDisabled={isDeleteEventButtonDisabled}
>
    {#if eventToDelete.eventType && eventToDelete.repeatIntervalType > 0}
        <p class="whitespace-pre-line mb-4">
            Do you want to delete:
        </p>
        <div class="flex flex-col gap-y-4 font-medium">
            <label class="flex items-center gap-4">
                <input
                    type="radio"
                    class="radio radio-secondary"
                    value={DeleteRepeatingEventOptions.thisOnly}
                    bind:group={deleteType}
                />
                This event only
            </label>
            <label class="flex items-center gap-4">
                <input
                    type="radio"
                    class="radio radio-secondary"
                    value={DeleteRepeatingEventOptions.thisAndFollowing}
                    bind:group={deleteType}
                />
                This and following events
            </label>
            <label class="flex items-center gap-4">
                <input
                    type="radio"
                    class="radio radio-secondary"
                    value={DeleteRepeatingEventOptions.allEvents}
                    bind:group={deleteType}
                />
                All events
            </label>
        </div>
    {:else}
        <p class="whitespace-pre-line">
            Do you want to delete this calendar event?
        </p>
    {/if}

</NotificationModal>
